import axios, { AxiosError } from 'axios';

import {
    ServiceResponse,
    V2ErrorResponse,
} from '../types/service-response.type';
import { CreateSubscription } from '../types/create-subscription.type';
import { PaginatedQuery } from '../types/paginated-query.type';
import { CreateService, Service } from '../types/service.type';
import { Subscription } from '../types/subscription.type';

import {
    addQueryParams,
    getAuthToken,
    handleAxiosError,
    handleResponseError,
    handleV2ResponseError,
} from './service';

const SUBSCRIPTIONS_BASE_URL = process.env.REACT_APP_SUBSCRIPTIONS_URL;

interface UpdateData {
    status?: number;
    suspendedAt?: string;
    expiresAt?: string;
    freeTowingServices?: number;
    freeAmbulanceServices?: number;
}

type QueryParams = {
    status: number;
} & PaginatedQuery;

const getServices = async (): Promise<ServiceResponse> => {
    console.log('SubscriptionService: getServices');
    try {
        const response = await axios.get(
            `${SUBSCRIPTIONS_BASE_URL}/api/v1/services/active`,
            { headers: { Authorization: getAuthToken() } }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const create = async (data: CreateSubscription): Promise<ServiceResponse> => {
    console.log('SubscriptionService: create');
    try {
        const response = await axios.post(
            `${SUBSCRIPTIONS_BASE_URL}/api/v1/subscriptions`,
            data,
            { headers: { Authorization: getAuthToken() } }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const updateSubscription = async (
    id: number,
    data: UpdateData
): Promise<ServiceResponse> => {
    console.log('SubscriptionService: update');
    try {
        // remove null/undefined values
        const payload = Object.fromEntries(
            Object.entries(data).filter(
                ([, value]) => value !== undefined && value !== null
            )
        );
        const response = await axios.patch(
            `${SUBSCRIPTIONS_BASE_URL}/api/admin/subscriptions/${id}`,
            payload,
            { headers: { Authorization: getAuthToken() } }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const getSubscriptionByAdmin = async (
    id: number
): Promise<Subscription | V2ErrorResponse> => {
    try {
        console.log('SubscriptionService: getSubscriptionByAdmin');
        const response = await axios.get(
            `${SUBSCRIPTIONS_BASE_URL}/api/admin/v2/subscriptions/${id}`,
            { headers: { Authorization: getAuthToken() } }
        );
        return response.data as Subscription;
    } catch (err) {
        return handleV2ResponseError(err as AxiosError);
    }
};

const pauseSubscription = async (
    id: number
): Promise<Subscription | V2ErrorResponse> => {
    try {
        console.log('SubscriptionService: pauseSubscription');
        const response = await axios.post(
            `${SUBSCRIPTIONS_BASE_URL}/api/admin/v2/subscriptions/${id}/pause`,
            {},
            { headers: { Authorization: getAuthToken() } }
        );
        return response.data;
    } catch (err) {
        return handleV2ResponseError(err as AxiosError);
    }
};

const resumeSubscription = async (
    id: number
): Promise<Subscription | V2ErrorResponse> => {
    try {
        console.log('SubscriptionService: resumeSubscription');
        const response = await axios.post(
            `${SUBSCRIPTIONS_BASE_URL}/api/admin/v2/subscriptions/${id}/resume`,
            {},
            { headers: { Authorization: getAuthToken() } }
        );
        return response.data;
    } catch (err) {
        return handleV2ResponseError(err as AxiosError);
    }
};

const cancelSubscription = async (
    id: number
): Promise<{ ok: true } | V2ErrorResponse> => {
    try {
        console.log('SubscriptionService: cancelSubscription');
        await axios.delete(
            `${SUBSCRIPTIONS_BASE_URL}/api/admin/v2/subscriptions/${id}`,
            { headers: { Authorization: getAuthToken() } }
        );
        return { ok: true };
    } catch (err) {
        return handleV2ResponseError(err as AxiosError);
    }
};

type PlanFilters = {
    active?: boolean;
    isPublic?: boolean;
    isForBusiness?: boolean;
};

const getPlans = async (filters?: PlanFilters): Promise<ServiceResponse> => {
    console.log('SubscriptionService: getPlans');
    try {
        const params = addQueryParams(filters);
        const response = await axios.get(
            `${SUBSCRIPTIONS_BASE_URL}/api/v1/plans${params}`,
            {
                headers: { Authorization: getAuthToken() },
            }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

type SubmitBulkUpload = {
    company?: number;
    action: number;
    planId?: number;
    file: File;
};

const bulkUpload = async (data: SubmitBulkUpload): Promise<ServiceResponse> => {
    const form = new FormData();
    form.append('file', data.file);
    form.append('action', data.action.toString());

    data.planId && form.append('planId', data.planId.toString());
    data.company && form.append('teamId', data.company.toString());

    try {
        const response = await axios.post(
            `${SUBSCRIPTIONS_BASE_URL}/api/admin/subscriptions/bulk-upload`,
            form,
            {
                headers: { Authorization: getAuthToken() },
            }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

type UserInclusion = {
    userId: number;
    companyId: number;
    planId: number;
};

const includeUserToCompany = async (
    data: UserInclusion
): Promise<ServiceResponse> => {
    try {
        const response = await axios.post(
            `${SUBSCRIPTIONS_BASE_URL}/api/admin/companies/${data.companyId}/users`,
            {
                userId: data.userId,
                planId: data.planId,
            },
            {
                headers: { Authorization: getAuthToken() },
            }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const getAllServices = async (
    queryParams?: QueryParams
): Promise<Service[]> => {
    try {
        const inlineParams = addQueryParams(queryParams);
        const url = `${SUBSCRIPTIONS_BASE_URL}/api/admin/services${inlineParams}`;

        const response = await axios.get(url, {
            headers: { Authorization: getAuthToken() },
        });
        return response.data;
    } catch (err) {
        console.error('Error getting all services:', err);
        return [];
    }
};

const createService = async (data: CreateService): Promise<ServiceResponse> => {
    try {
        const response = await axios.post(
            `${SUBSCRIPTIONS_BASE_URL}/api/admin/services`,
            data,
            { headers: { Authorization: getAuthToken() } }
        );

        if (response.status !== 201) throw new Error(response.data.error);

        return { code: 201, message: '', data: response.data };
    } catch (err) {
        return handleAxiosError(err as AxiosError);
    }
};

const updateService = async (
    id: number,
    data: CreateService
): Promise<ServiceResponse> => {
    try {
        const response = await axios.put(
            `${SUBSCRIPTIONS_BASE_URL}/api/admin/services/${id}`,
            data,
            { headers: { Authorization: getAuthToken() } }
        );
        return { code: 200, message: '', data: response.data };
    } catch (err) {
        return handleAxiosError(err as AxiosError);
    }
};

export const subscriptionSdk = {
    getServices,
    create,
    updateSubscription,
    getSubscriptionByAdmin,
    pauseSubscription,
    resumeSubscription,
    cancelSubscription,
    getPlans,
    bulkUpload,
    includeUserToCompany,
    getAllServices,
    createService,
    updateService,
};
